<template>
  <div class="container">
    <div v-for="(item, index) in formData.branches" :key="index">
      <t-form-item class="info">
        <template v-if="index === 0" #label>
          <div class="collapseLabel">
            <div>
              <text-with-mark :text="$t('条件配置')" />
            </div>
            <div>
              <t-button
                variant="text"
                theme="primary"
                style="padding-right: 0; margin-right: 10px"
                @click="collapseAll"
              >
                {{ $t("收起全部") }}
              </t-button>
              <t-button
                variant="text"
                theme="primary"
                style="padding-right: 0"
                @click="expandAll"
              >
                {{ $t("展开全部") }}</t-button
              >
            </div>
          </div>
        </template>
        <t-collapse :value="expandItems" borderless @change="handlePanelChange">
          <t-collapse-panel
            :value="`${index}`"
            :header="$t('条件组') + ' ' + (index + 1)"
          >
            <template #headerRightContent>
              <CloseCircleIcon
                :disabled="formData.branches.length <= 1"
                size="16"
                style="padding-left: 8px; vertical-align: sub"
                @click="deleteConditionGroup(index)"
              />
            </template>

            <t-form-item
              v-if="formData.branches[index].condition?.length > 1"
              :name="`branches[${index}].type`"
              :status-icon="false"
              :rules="requireRule"
              :required-mark="false"
            >
              <t-radio-group
                :value="formData.branches[index].type"
                style="margin-bottom: 10px"
                @change="onTypeChange(index, $event)"
              >
                <t-radio :value="1"> {{ $t("并且") }}</t-radio>
                <t-radio :value="2"> {{ $t("或者") }}</t-radio>
              </t-radio-group>
            </t-form-item>

            <div
              v-for="(conditionItem, itemIndex) in formData.branches[index][
                'condition'
              ]"
              :key="itemIndex"
            >
              <t-divider v-if="itemIndex > 0" align="left">
                <t-tag theme="success" shape="round" color="#00B89C">
                  {{
                    formData.branches[index].type === 2
                      ? $t("或者")
                      : $t("并且")
                  }}
                </t-tag>
              </t-divider>
              <div class="condition">
                <div>
                  <div>
                    <t-space :size="8">
                      <t-form-item
                        :name="`branches[${index}].condition[${itemIndex}].variableKey`"
                        :rules="requireRule"
                        :required-mark="false"
                        style="margin-bottom: 8px"
                      >
                        <select-with-operate
                          v-model="
                            formData.branches[index]['condition'][itemIndex][
                              'variableKey'
                            ]
                          "
                          with-system
                        />
                      </t-form-item>
                      <t-form-item
                        :name="`branches[${index}].condition[${itemIndex}].relation`"
                        :rules="requireRule"
                        :required-mark="false"
                      >
                        <t-select
                          :options="relationOptions"
                          :value="
                            (formData.branches[index]['condition'][itemIndex] ??
                              {})['relation']
                          "
                          style="width: 157px"
                          @change="onRelationChange(index, itemIndex, $event)"
                        />
                      </t-form-item>
                    </t-space>
                  </div>
                  <div
                    v-if="
                      !['not_exist', 'exist'].includes(
                        formData.branches[index]['condition'][itemIndex]
                          .relation,
                      )
                    "
                    style="margin-top: 10px"
                  >
                    <t-form-item
                      :name="`branches[${index}].condition[${itemIndex}].value`"
                      :rules="requireRule"
                      :required-mark="false"
                    >
                      <t-input
                        v-model="
                          formData.branches[index]['condition'][itemIndex][
                            'value'
                          ]
                        "
                        maxlength="100"
                        style="width: 358px"
                        :placeholder="$t('填写常量')"
                      />
                    </t-form-item>
                  </div>
                </div>
                <div>
                  <CloseCircleIcon
                    v-if="(formData.branches[index].condition ?? []).length > 1"
                    size="16"
                    style="cursor: pointer"
                    @click="deleteCondition(index, itemIndex)"
                  />
                </div>
              </div>
            </div>

            <t-form-item
              v-if="(formData.branches[index]['condition'] ?? []).length < 10"
              :status-icon="false"
              style="padding-top: 8px"
            >
              <t-link
                theme="primary"
                hover="color"
                @click="addCondition(index)"
              >
                {{ $t("添加条件") }}
              </t-link>
            </t-form-item>
          </t-collapse-panel>
        </t-collapse>
      </t-form-item>
    </div>
    <t-form-item v-if="formData.branches.length < 10" :status-icon="false">
      <t-space size="small">
        <t-link theme="primary" hover="color" @click="addConditionGroup">
          {{ $t("添加条件组") }}
        </t-link>
      </t-space>
    </t-form-item>
  </div>
</template>

<script lang="ts" setup>
import { reactive, ref, watch } from "vue";
import { CloseCircleIcon } from "tdesign-icons-vue-next";
import { map, merge, trim } from "lodash-es";
import { getRelationUUID } from "@/utils";
import { t } from "@/utils/i18n";

const emit = defineEmits(["updateData"]);

const relationOptions = [
  { value: "contain", label: t("包含") },
  { value: "not_contain", label: t("不包含") },
  { value: "==", label: t("等于") },
  { value: "!=", label: t("不等于") },
  { value: "not_exist", label: t("为空") },
  { value: "exist", label: t("不为空") },
];

const requireRule = [
  {
    validator: (str) => {
      return !!trim(str);
    },
    message: t("不能为空"),
    trigger: "change",
  },
];

type RelationSymbol = (typeof relationOptions)[number]["value"];

type BranchProp = Array<{
  id: string;
  type: 1 | 2; // 1并且 2或者
  content: string;
  condition: Array<{
    relation: RelationSymbol;
    variableKey: string;
    value: string;
  }>;
}>;

const props = defineProps<{
  data: {
    branches: BranchProp;
  };
}>();

type Model = {
  nodeData: {
    branches: BranchProp;
  };
};

const formData = reactive<Model["nodeData"]>({
  branches: props.data.branches,
});

const expandItems = ref(["0"]);

const handlePanelChange = (val) => {
  expandItems.value = val;
};

const onRelationChange = (index, itemIndex, v) => {
  formData.branches[index]["condition"][itemIndex]["relation"] = v;
  formData.branches[index]["condition"][itemIndex]["value"] = "";
};

const collapseAll = () => {
  expandItems.value = [];
};

const expandAll = () => {
  expandItems.value = map(formData.branches, (v, index) => `${index}`);
};

const addConditionGroup = () => {
  formData.branches.push({
    id: getRelationUUID(),
    content: t("条件组"),
    condition: [
      {
        relation: "==",
        variableKey: "",
        value: "",
      },
    ],
    type: 1,
  });
};

const deleteConditionGroup = (index: number) => {
  if (formData.branches.length <= 1) return;
  formData.branches.splice(index, 1);
};

const deleteCondition = (index: number, itemIndex: number) => {
  formData.branches[index].condition?.splice(itemIndex, 1);
};

const addCondition = (index: number) => {
  (formData.branches[index].condition ?? []).push({
    relation: "==",
    variableKey: "",
    value: "",
  });
};

const onTypeChange = (index, value) => {
  formData.branches[index].type = value;
};

watch(formData, (newVal) => {
  emit(
    "updateData",
    merge({}, newVal, {
      branches: map(newVal.branches, (v, index) => ({
        ...v,
        content: t("条件组") + " " + (index + 1),
      })),
    }),
  );
});
</script>
<style lang="less" scoped>
.container {
  :deep(.flow-t-collapse.flow-t--border-less) {
    width: 100%;
  }
  :deep(.flow-t-collapse-panel__wrapper) {
    background-color: #f9fafc;
    margin-bottom: 15px;
    border-radius: 8px;
    border: 1px solid #e4e8ee;
    :deep(.flow-t-collapse-panel__header) {
      padding-left: 0px !important;
    }
  }
  :deep(.flow-t-collapse-panel__wrapper .flow-t-collapse-panel__content) {
    padding-left: 16px;
    padding-top: 0px;
  }
  :deep(.flow-t-collapse.flow-t--border-less .flow-t-collapse-panel__body) {
    background-color: #f9fafc;
  }
  :deep(.flow-t-divider--with-text-left::before) {
    width: 0%;
  }
  :deep(.flow-t-divider--with-text-left::after) {
    width: 100%;
  }
  :deep(.flow-t-divider--with-text .flow-t-divider__inner-text:not(:empty)) {
    padding: 0px 10px 0px 0px;
  }
  :deep(.flow-t-button--variant-text:hover) {
    border-color: transparent;
    background-color: transparent;
  }
}
.condition {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.info {
  :deep(.flow-t-form__label) {
    padding-right: 0px;
  }
}
.collapseLabel {
  display: flex;
  justify-content: space-between;
}
</style>
